import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  IonModal,
  IonContent,
  IonButton,
  IonIcon,
  IonSpinner,
  IonCard,
  IonList,
  IonItem,
  IonLabel,
  IonAvatar,
  IonText,
  IonRow,
  IonCol,
  IonGrid,
  IonInput,
  IonRadio,
} from '@ionic/react';
import {
  chevronDownOutline,
  personCircleOutline,
  searchOutline,
  calendarOutline,
  cardOutline,
  businessOutline,
  checkmarkCircleOutline,
  informationCircleOutline,
  timeOutline,
  pricetagOutline,
  refreshOutline,
  exitOutline,
  closeOutline,
} from 'ionicons/icons';
import type { HelcimPaymentPlan, HelcimCustomer, HelcimSubscription } from '@rsmaids/shared/services/api/helcim/helcim';
import MiniCalendarPicker from './MiniCalendarPicker';
import { searchHelcimCustomers, createSubscription, updateSubscriptionFields } from '../services/helcimInvoicingService';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { proxyImage, getDefaultAvatar } from '@rsmaids/shared/utils/avatarUtils';
import type { ContactListItem } from '@rsmaids/shared/types/contacts';
import { getCachedContactList } from '../utils/contactCache';
import BasePageWithHeader from './BasePageWithHeader';
import styles from './AddSubscriberModal.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { translateDynamic } from '../i18n/dynamic';
import { intlLocale } from '../i18n/locale';
import { dbContacts } from '../auth/firebase';
import { collection, query, where, limit, getDocs, doc, setDoc } from 'firebase/firestore';

// This modal is a general subscriber picker (opened from the subscriptions
// list / plan detail, never from one specific contact's page), so it only
// ever has the Helcim customer object — never the Wix contactId that
// contactBilling/{id} is keyed by. One indexed query resolves it. Run
// fire-and-forget AFTER the subscription create already succeeded and the
// success screen is shown — never block the user on this.
//
// A subscription existing is the ground truth for how this client bills:
// Helcim always builds its own recurring invoices tax-exclusive (confirmed
// against 5 months of a real customer's invoices — see
// functions/src/services/zohoServiceOrderService.ts's isHelcimSubscriber doc
// comment), and Complete must never also create a duplicate per-order invoice
// for a client Helcim already bills on its own schedule. So these 3 fields are
// always force-set on subscribe, overwriting whatever was there before.
async function autoSetHelcimSubscriberBilling(customerCode: string | undefined): Promise<void> {
  if (!customerCode) return;
  try {
    const snap = await getDocs(query(
      collection(dbContacts, 'contacts'),
      where('customerCode', '==', customerCode),
      limit(1)
    ));
    const contactId = snap.docs[0]?.id;
    if (!contactId) return;
    await setDoc(doc(dbContacts, 'contactBilling', contactId), {
      isInclusiveTax: false,
      isHelcimSubscriber: true,
      invoiceMode: 'manual',
    }, { merge: true });
  } catch (e) {
    // Non-fatal — the subscription itself already succeeded. Billing config
    // can still be set manually from the contact's Billing Config screen.
    console.error('[AddSubscriberModal] Failed to auto-set subscriber billing config:', e);
  }
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function displayDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
}

function addPeriod(date: Date, period: string, inc: number): Date {
  const d = new Date(date);
  switch (period) {
    case 'daily': d.setDate(d.getDate() + inc); break;
    case 'weekly': d.setDate(d.getDate() + 7 * inc); break;
    case 'monthly': d.setMonth(d.getMonth() + inc); break;
    case 'yearly': d.setFullYear(d.getFullYear() + inc); break;
  }
  return d;
}

function periodLabel(period: string, inc: number): string {
  const p = period?.charAt(0).toUpperCase() + period?.slice(1);
  if (!inc || inc === 1) return translateDynamic(p ?? '');
  const suffix: Record<string, string> = {
    daily: 'Days', weekly: 'Weeks', monthly: 'Months', yearly: 'Years',
  };
  return i18n.t('components.AddSubscriberModal.every', { inc, v2: translateDynamic(suffix[period] ?? p) });
}

/**
 * Given the activation date and the plan's dateBilling, compute the actual first
 * billing date. For 'Sign-up' / 'Daily' plans it equals the activation date.
 * For day-of-month plans (e.g. '11') it finds the next occurrence of that day.
 */
function computeFirstBillingDate(activationIso: string, dateBilling: string | undefined): Date {
  const activation = new Date(activationIso + 'T12:00:00');
  let db = (dateBilling ?? 'Sign-up').trim();

  // Full ISO date (e.g. "2026-05-11") — extract just the day-of-month
  if (/^\d{4}-\d{2}-\d{2}$/.test(db)) {
    db = String(parseInt(db.slice(8), 10)); // e.g. "11"
  }

  // Sign-up billing: first charge on activation day
  if (!db || db === 'Sign-up' || db === 'Daily') return activation;

  // Numeric day-of-month (e.g. "11" = bill on the 11th each month)
  const dayNum = parseInt(db, 10);
  if (!isNaN(dayNum)) {
    // Try the billing day in the current month first
    const candidate = new Date(activation.getFullYear(), activation.getMonth(), dayNum, 12);
    if (candidate >= activation) return candidate;
    // Missed this month — use next month (always +1 month, not +inc)
    return new Date(activation.getFullYear(), activation.getMonth() + 1, dayNum, 12);
  }

  // Weekday name (e.g. "Monday")
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const targetDay = weekdays.indexOf(db);
  if (targetDay !== -1) {
    const d = new Date(activation);
    const diff = (targetDay - d.getDay() + 7) % 7;
    // diff === 0 means activation day IS the billing weekday — bill today
    d.setDate(d.getDate() + diff);
    return d;
  }

  // MM-DD fixed date (e.g. "06-11" = June 11 each year)
  if (/^\d{2}-\d{2}$/.test(db)) {
    const [mm, dd] = db.split('-').map(Number);
    const candidate = new Date(activation.getFullYear(), (mm ?? 1) - 1, dd ?? 1, 12);
    if (candidate >= activation) return candidate;
    return new Date(activation.getFullYear() + 1, (mm ?? 1) - 1, dd ?? 1, 12);
  }

  // Fallback: first billing = activation
  return activation;
}

// ─── portal-anchored dropdown (same mechanism as HelcimSubscriptionsModal.tsx's
// usePortalDropdownPosition/PortalDropdown) — replaces the plan/price native
// <select> elements with the app's standard pill-trigger + glass dropdown card. ──

interface DropdownOption {
  label: string;
  value: string;
  sublabel?: string;
}

type PortalDropdownRect = { top: number; left?: number; right?: number; width?: number };

function usePortalDropdownPosition(
  isOpen: boolean,
  anchorRef: React.RefObject<HTMLElement | null>,
  portalRef: React.RefObject<HTMLElement | null>,
  cardRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
  matchWidth = true,
) {
  const [rect, setRect] = useState<PortalDropdownRect | null>(null);
  // Clear the computed rect the moment the dropdown closes — computed
  // during render rather than as a synchronous setState call at the top of
  // the effect below.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (!isOpen && rect !== null) setRect(null);
  }

  useEffect(() => {
    if (!isOpen) return;
    const computeRect = (): PortalDropdownRect | null => {
      const field = anchorRef.current;
      const portal = portalRef.current;
      if (!field || !portal) return null;
      const box = field.getBoundingClientRect();
      const anchor = portal.getBoundingClientRect();
      const top = box.bottom - anchor.top + 4;
      return { top, left: box.left - anchor.left, width: matchWidth ? box.width : undefined };
    };
    const applyRect = (r: PortalDropdownRect) => {
      const card = cardRef.current as HTMLElement | null;
      if (!card) return;
      card.style.top = `${r.top}px`;
      card.style.left = r.left !== undefined ? `${r.left}px` : '';
      card.style.width = r.width !== undefined ? `${r.width}px` : '';
    };

    const initial = computeRect();
    setRect(initial);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const r = computeRect();
        if (r) { setRect(r); applyRect(r); }
      });
    });

    const handleScroll = () => { const r = computeRect(); if (r) applyRect(r); };
    const handleResize = () => { const r = computeRect(); if (r) { setRect(r); applyRect(r); } };
    const handleOutsidePointer = (e: PointerEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (portalRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('pointerdown', handleOutsidePointer);
    };
  }, [isOpen, anchorRef, portalRef, cardRef, onClose, matchWidth]);

  return rect;
}

const PortalDropdown: React.FC<{
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  portalRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  options: DropdownOption[];
  value?: string;
  onSelect: (value: string) => void;
  matchWidth?: boolean;
}> = ({ isOpen, anchorRef, portalRef, onClose, options, value, onSelect, matchWidth = true }) => {
  const cardRef = useRef<HTMLIonCardElement>(null);
  const rect = usePortalDropdownPosition(isOpen, anchorRef, portalRef, cardRef, onClose, matchWidth);

  // portalRef's target renders alongside this component, so its .current is
  // only reliably non-null after this component itself has committed at
  // least once — track that in state instead of reading .current directly
  // during render.
  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(portalRef.current);
  }, [portalRef, isOpen]);

  if (!isOpen || !rect || !portalNode) return null;

  return createPortal(
    <>
      <div className={styles.dropdownBackdrop} onClick={onClose} />
      <IonCard ref={cardRef} className={styles.customDropdownList} style={{ ...rect, position: 'absolute' }}>
        <IonList lines="none" className={styles.customDropdownListInner}>
          {options.map(opt => (
            <IonItem
              key={opt.value}
              button
              lines="none"
              detail={false}
              className={`${styles.customDropdownItem} ${opt.value === value ? styles.customDropdownItemActive : ''}`}
              onClick={() => onSelect(opt.value)}
            >
              <IonLabel>{opt.label}</IonLabel>
              {opt.sublabel && <IonText slot="end" className={styles.customDropdownSublabel}>{opt.sublabel}</IonText>}
            </IonItem>
          ))}
        </IonList>
      </IonCard>
    </>,
    portalNode,
  );
};

// ─── component ────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  plan?: HelcimPaymentPlan;
  allPlans?: HelcimPaymentPlan[];
  merchantTaxRate?: number | null;
  /** If set, the modal opens in edit mode pre-filled with this subscription */
  editSubscription?: HelcimSubscription | null;
  /** Pre-filled customer object for edit mode */
  editCustomer?: HelcimCustomer | null;
  onClose: () => void;
  onSubscriberAdded: () => void;
}

const AddSubscriberModal: React.FC<Props> = ({ isOpen, plan, allPlans, merchantTaxRate: _merchantTaxRate, editSubscription, editCustomer, onClose, onSubscriberAdded }) => {
  const { t } = useTranslation();
  // Customer search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ContactListItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<HelcimCustomer | null>(null);
  const [searchActive, setSearchActive] = useState(false);
  // Avatar of the picked contact (same image the search results show)
  const [selectedAvatar, setSelectedAvatar] = useState('');

  // Wix contacts (cached list) — loaded once on open for instant client-side filtering.
  // The Helcim customer is resolved from the chosen contact, not searched directly.
  const [allContacts, setAllContacts] = useState<ContactListItem[]>([]);
  const allContactsLoaded = useRef(false);

  // Snapshot of props the reset/prefill effect below reads without becoming
  // reactive to every one of their changes — it's deliberately scoped to run
  // only on isOpen/plan?.id transitions. Kept current via a plain per-render
  // effect (no dep array) rather than exhaustive-deps on the effect itself,
  // which would defeat that deliberate open-only behavior.
  const latestForResetEffectRef = useRef({ plan, allPlans, editSubscription, editCustomer });
  useEffect(() => {
    latestForResetEffectRef.current = { plan, allPlans, editSubscription, editCustomer };
  });

  // Pricing — always derived from plan prop; user may override to a different plan from allPlans
  const [selectedPlan, setSelectedPlan] = useState<HelcimPaymentPlan | null>(null);
  const [useCustom, setUseCustom] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [customSetupFee, setCustomSetupFee] = useState('');

  // Plan / price portal dropdowns
  const [showPlanDropdown, setShowPlanDropdown] = useState(false);
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const planTriggerRef = useRef<HTMLButtonElement>(null);
  const priceTriggerRef = useRef<HTMLButtonElement>(null);
  const pickerPortalRef = useRef<HTMLDivElement>(null);
  // pickerPortalRoot (rendered inside the IonModal below) only exists while the
  // modal is open, which can be well after this component first mounts — a
  // mount-only read of pickerPortalRef.current left this null forever, so the
  // customer-search and date dropdowns (the inline createPortal calls further
  // down) never rendered. A callback ref tracks the node whenever it actually
  // mounts/unmounts, and keeps pickerPortalRef populated for the hooks above.
  const [pickerPortalNode, setPickerPortalNode] = useState<HTMLDivElement | null>(null);
  const setPickerPortalEl = useCallback((el: HTMLDivElement | null) => {
    pickerPortalRef.current = el;
    setPickerPortalNode(el);
  }, []);

  // Schedule
  const [activationDate, setActivationDate] = useState(fmtDate(new Date()));
  const [editMaxCycles, setEditMaxCycles] = useState<string>('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateTriggerRef = useRef<HTMLButtonElement>(null);
  const dateDropdownCardRef = useRef<HTMLIonCardElement>(null);

  // Payment method
  const [payMethod, setPayMethod] = useState<'credit' | 'ach'>('credit');
  // Standalone <ion-radio> (no IonRadioGroup) genuinely has a `checked`
  // property on the underlying custom element, but @ionic/react's generated
  // React wrapper omits it from IonRadio's prop types (it's designed to be
  // managed by IonRadioGroup instead) — set it imperatively via ref rather
  // than fighting the type system with a cast.
  const creditRadioRef = useRef<HTMLIonRadioElement>(null);
  const achRadioRef = useRef<HTMLIonRadioElement>(null);
  useEffect(() => {
    // HTMLIonRadioElement's TS type omits `checked` even though the
    // underlying custom element genuinely has it (confirmed in
    // @ionic/core/dist/types/components/radio/radio.d.ts) — narrow cast for
    // just this property rather than widening to `any`.
    if (creditRadioRef.current) (creditRadioRef.current as unknown as { checked: boolean }).checked = payMethod === 'credit';
    if (achRadioRef.current) (achRadioRef.current as unknown as { checked: boolean }).checked = payMethod === 'ach';
  }, [payMethod]);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Fresh payment methods (fetched on customer select, not cached from search)
  const [freshCards, setFreshCards] = useState<Record<string, unknown>[]>([]);
  const [freshBanks, setFreshBanks] = useState<Record<string, unknown>[]>([]);
  const [loadingCards, setLoadingCards] = useState(false);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const customerFieldRef = useRef<HTMLIonItemElement>(null);
  const customerDropdownCardRef = useRef<HTMLIonCardElement>(null);

  // Load the Wix contact list once on open
  useEffect(() => {
    if (!isOpen || allContactsLoaded.current) return;
    allContactsLoaded.current = true;
    setSearching(true);
    const svc = DataServiceFactory.getService();
    getCachedContactList(() => svc.contacts.getAllContacts())
      .then(result => setAllContacts([...result.data].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))))
      .catch(() => { allContactsLoaded.current = false; })
      .finally(() => setSearching(false));
  }, [isOpen]);

  // Reset on open/close. Deferred via a resolved-promise microtask (not
  // called synchronously in the effect body) — this logic is entirely
  // synchronous itself, but the effect-setState rule flags any setState
  // reachable directly at an effect's top level.
  useEffect(() => { Promise.resolve().then(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setShowDropdown(false);
      setSearching(false);
      setSelectedCustomer(null);
      setSelectedAvatar('');
      setSearchActive(false);
      setFreshCards([]);
      setFreshBanks([]);
      setLoadingCards(false);
      setUseCustom(false);
      setCustomAmount('');
      setCustomSetupFee('');
      setActivationDate(fmtDate(new Date()));
      setEditMaxCycles('');
      setPayMethod('credit');
      setSubmitting(false);
      setError('');
      setSuccess(false);
      setAllContacts([]);
      allContactsLoaded.current = false;
      setSelectedPlan(null);
      return;
    }
    const { plan, allPlans, editSubscription, editCustomer } = latestForResetEffectRef.current;
    // In edit mode sync to the subscription's plan; in create mode default to the passed-in plan
    if (editSubscription) {
      const matchedPlan = allPlans?.find(p => p.id === editSubscription.paymentPlanId) ?? plan;
      setSelectedPlan(matchedPlan ?? null);
    } else {
      setSelectedPlan(plan ?? null);
    }

    // ── Edit mode: pre-fill from existing subscription ──────────────────
    if (editSubscription) {
      setSearchActive(true); // customer chip is shown, no need for button
      if (editCustomer) {
        setSelectedCustomer(editCustomer);
        // Fetch fresh payment methods for the pre-filled customer
        const code = editCustomer.customerCode;
        if (code) {
          setFreshCards([]);
          setFreshBanks([]);
          setLoadingCards(true);
          searchHelcimCustomers(code).then((results: HelcimCustomer[]) => {
            const match = results.find((r: HelcimCustomer) => r.customerCode === code) ?? results[0];
            if (match) {
              const raw = match as unknown as Record<string, unknown>;
              const cards = (raw['cards'] as Record<string, unknown>[] | undefined) ?? [];
              const banks = (raw['bankAccounts'] as Record<string, unknown>[] | undefined) ?? [];
              setFreshCards(cards);
              setFreshBanks(banks);
              // In edit mode, use the subscription's current paymentMethod; otherwise default to card if available
              const subPayMethod = (editSubscription as Record<string, unknown> | null)?.['paymentMethod'] as string | undefined;
              if (editSubscription && subPayMethod) {
                setPayMethod(subPayMethod === 'bank' ? 'ach' : 'credit');
              } else {
                setPayMethod(cards.length > 0 ? 'credit' : banks.length > 0 ? 'ach' : 'credit');
              }
            }
          }).catch(() => { }).finally(() => setLoadingCards(false));
        }
      }
      // Pre-fill custom amount if it differs from the plan's recurring amount
      const subAmount = editSubscription.recurringAmount;
      const currentPlan = allPlans?.find(p => p.id === editSubscription.paymentPlanId) ?? plan;
      const planAmt = currentPlan?.recurringAmount ?? 0;
      if (subAmount != null && subAmount !== planAmt) {
        setUseCustom(true);
        setCustomAmount(String(subAmount));
      }
      // Pre-fill activation date from the original dateActivated, fall back to dateBilling
      if (editSubscription.dateActivated) setActivationDate(editSubscription.dateActivated);
      else if (editSubscription.dateBilling) setActivationDate(editSubscription.dateBilling);
      // Pre-fill max cycles
      if (editSubscription.maxCycles != null && editSubscription.maxCycles > 0) {
        setEditMaxCycles(String(editSubscription.maxCycles));
      }
    }
  }); }, [isOpen, plan?.id]);

  // Debounced search over the Wix contacts
  const runSearch = useCallback((q: string) => {
    const ql = q.trim().toLowerCase();
    if (!ql) {
      setSearchResults(allContacts.slice(0, 40));
      setShowDropdown(allContacts.length > 0);
      return;
    }
    const filtered = allContacts.filter(c =>
      (c.displayName ?? '').toLowerCase().includes(ql) ||
      `${c.firstName ?? ''} ${c.lastName ?? ''}`.toLowerCase().includes(ql) ||
      (c.company ?? '').toLowerCase().includes(ql) ||
      (c.primaryEmail ?? '').toLowerCase().includes(ql) ||
      (c.primaryPhone ?? '').toLowerCase().includes(ql) ||
      (c.customerCode ?? '').toLowerCase().includes(ql)
    ).slice(0, 40);
    setSearchResults(filtered);
    setShowDropdown(true);
  }, [allContacts]);

  // Re-run whatever search is currently showing once the contact list
  // finishes loading. Without this, clicking "Add Customer" right after the
  // modal opens fires runSearch('') while allContacts is still empty, shows
  // nothing, and never gets a second chance once the cache actually arrives —
  // the dropdown just silently never appears until the user types.
  useEffect(() => {
    if (!searchActive) return;
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — runSearch's own setState calls are needed on
    // every real invocation (including the per-keystroke search handler
    // elsewhere), so they can't just be dropped the way a mount-only
    // redundant reset could be.
    Promise.resolve().then(() => runSearch(searchQuery));
  }, [allContacts, runSearch, searchActive, searchQuery]);

  const handleSearchChange = (q: string) => {
    setSearchQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runSearch(q), 80);
  };

  // Pick a contact from the Firestore contact list. The contact already carries its
  // Helcim customerCode, so the selected customer is built from the contact itself —
  // nothing is looked up in Helcim to identify them.
  const selectCustomer = (contact: ContactListItem) => {
    setSearchQuery('');
    setShowDropdown(false);
    setSearchResults([]);
    setError('');
    setFreshCards([]);
    setFreshBanks([]);
    setSelectedAvatar(contact.avatar ?? '');
    const code = contact.customerCode;
    if (!code) {
      setSelectedCustomer(null);
      setError(t('components.AddSubscriberModal.noHelcimCustomerForContact'));
      return;
    }
    setSelectedCustomer({
      id: 0,
      customerCode: code,
      contactName: contact.displayName || `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || contact.company || '',
      businessName: contact.company,
      dateCreated: '',
      dateUpdated: '',
      billingAddress: { email: contact.primaryEmail },
    });
    // Saved cards/bank accounts only exist in Helcim, so they're loaded in the
    // background by customerCode — for the payment-method picker only.
    setLoadingCards(true);
    searchHelcimCustomers(code).then((results: HelcimCustomer[]) => {
      const match = results.find((r: HelcimCustomer) => r.customerCode === code);
      if (!match) return;
      const raw = match as unknown as Record<string, unknown>;
      const cards = (raw['cards'] as Record<string, unknown>[] | undefined) ?? [];
      const banks = (raw['bankAccounts'] as Record<string, unknown>[] | undefined) ?? [];
      setFreshCards(cards);
      setFreshBanks(banks);
      // Auto-select appropriate method: prefer card, fall back to bank
      setPayMethod(cards.length > 0 ? 'credit' : banks.length > 0 ? 'ach' : 'credit');
    }).catch(() => {/* silently ignore */ }).finally(() => setLoadingCards(false));
  };

  // Anchored portal position for the customer search results — same mechanism
  // as the plan/price PortalDropdowns, closing on outside click included.
  // onClose must stay referentially stable: the plan/price dropdowns call this
  // hook from inside their own isolated <PortalDropdown> child, so a fresh
  // arrow function each render only re-renders that leaf. This is called
  // directly in AddSubscriberModal's body instead, so the hook's setRect(...)
  // re-renders this component itself — an inline arrow here would recreate
  // itself every render, re-fire the effect, call setRect again, and loop.
  const closeCustomerDropdown = useCallback(() => setShowDropdown(false), []);
  const customerDropdownRect = usePortalDropdownPosition(
    showDropdown, customerFieldRef, pickerPortalRef, customerDropdownCardRef, closeCustomerDropdown,
  );

  // Anchored portal position for the activation-date calendar — replaces the
  // native <input type="date"> with the same MiniCalendarPicker used by
  // CreateInvoice.tsx's DateField. Same referentially-stable-onClose caveat
  // as closeCustomerDropdown above.
  const closeDatePicker = useCallback(() => setShowDatePicker(false), []);
  const dateDropdownRect = usePortalDropdownPosition(
    showDatePicker, dateTriggerRef, pickerPortalRef, dateDropdownCardRef, closeDatePicker, false,
  );

  // ─── derived values ──────────────────────────────────────────────────────────

  const isEditMode = !!editSubscription;
  // True only when the sub has explicitly never been billed (timesBilled must be exactly 0)
  // A subscription is "not yet activated" only if dateActivated is absent or in the future
  const _todayStr = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const _activatedOn = editSubscription?.dateActivated;
  const isNotYetActivated = isEditMode && (!_activatedOn || _activatedOn > _todayStr);

  const isForever = !selectedPlan?.termType || selectedPlan.termType === 'forever';
  const planAmount = selectedPlan?.recurringAmount ?? 0;
  const setupFee = selectedPlan?.setupAmount ?? 0;
  const resolvedSetupFee = customSetupFee !== '' ? (parseFloat(customSetupFee) || 0) : setupFee;
  const baseAmount = useCustom && customAmount ? parseFloat(customAmount) || planAmount : planAmount;

  // Tax rate is pre-computed in the function from Firestore config based on plan taxType+taxCalculation
  const TAX_RATE = selectedPlan && typeof selectedPlan['taxRate'] === 'number' ? (selectedPlan['taxRate'] as number) : 0;
  const taxAmount = TAX_RATE > 0 ? baseAmount * TAX_RATE : 0;
  const effectiveAmount = baseAmount + taxAmount;
  const firstChargeAmount = effectiveAmount + resolvedSetupFee;

  const period = selectedPlan?.billingPeriod ?? 'monthly';
  const inc = selectedPlan?.billingPeriodIncrements ?? 1;
  const dateBilling = selectedPlan?.dateBilling as string | undefined;

  // Actual first billing date based on plan's billing day schedule
  const firstBill = computeFirstBillingDate(activationDate, dateBilling);
  const secondBill = addPeriod(firstBill, period, inc);
  const thirdBill = addPeriod(firstBill, period, inc * 2);

  // Whether activation date == first billing (sign-up billing) or deferred
  const activationIsBillingDay = fmtDate(firstBill) === activationDate;

  // Expiry date: matches Helcim — originalActivation + (maxCycles * period) - 1 day
  // In edit mode use dateActivated from the subscription, not the pre-filled next billing date
  const originalActivationDate = isEditMode && editSubscription?.dateActivated
    ? editSubscription.dateActivated
    : activationDate;
  const maxCyclesNum = !isForever && editMaxCycles ? parseInt(editMaxCycles, 10) || 0 : 0;
  const expiryDate = maxCyclesNum > 0
    ? (() => {
      const d = addPeriod(new Date(originalActivationDate + 'T12:00:00'), period, inc * maxCyclesNum);
      d.setDate(d.getDate() - 1);
      return d;
    })()
    : null;

  // Number of cycles for timeline (create mode uses editMaxCycles when plan is not forever)
  const displayCycles = !isForever && editMaxCycles ? parseInt(editMaxCycles, 10) || 1 : null;

  const canSubmit = !!selectedCustomer && !!activationDate && !!selectedPlan && !submitting;

  // ─── submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!selectedCustomer || !selectedPlan) return;
    setSubmitting(true);
    setError('');
    try {
      if (isEditMode && editSubscription) {
        // Build only the changed fields
        const fields: { recurringAmount?: number; paymentPlanId?: number; maxCycles?: number; dateActivated?: string; paymentMethod?: 'card' | 'bank' } = {};
        if (selectedPlan.id !== editSubscription.paymentPlanId) fields.paymentPlanId = selectedPlan.id;
        const newAmount = useCustom && customAmount ? parseFloat(customAmount) : undefined;
        if (newAmount != null && newAmount !== (editSubscription.recurringAmount ?? selectedPlan.recurringAmount ?? 0)) {
          fields.recurringAmount = newAmount;
        }
        // dateActivated only patchable before first billing
        const origActivation = editSubscription.dateActivated ?? editSubscription.dateBilling ?? '';
        if (isNotYetActivated && activationDate && activationDate !== origActivation) fields.dateActivated = activationDate;
        // Send maxCycles when term is set
        if (!isForever && editMaxCycles !== '') {
          const newCycles = parseInt(editMaxCycles, 10);
          if (!isNaN(newCycles) && newCycles !== editSubscription.maxCycles) fields.maxCycles = newCycles;
        }
        // Payment method change
        const currentPayMethod = payMethod === 'ach' ? 'bank' : 'card';
        const origPayMethod = (editSubscription as Record<string, unknown>)['paymentMethod'] as string | undefined;
        if (origPayMethod && currentPayMethod !== origPayMethod) fields.paymentMethod = currentPayMethod;
        if (Object.keys(fields).length === 0) {
          setSuccess(true);
          return;
        }
        // Coerce ID to number explicitly — Firebase callable can deserialize it as string
        await updateSubscriptionFields(Number(editSubscription.id), fields);
      } else {
        const paymentMethod: 'card' | 'bank' = payMethod === 'ach' ? 'bank' : 'card';
        await createSubscription({
          paymentPlanId: selectedPlan.id,
          customerCode: selectedCustomer.customerCode,
          dateActivated: activationDate,
          paymentMethod,
          ...(useCustom && customAmount ? { recurringAmount: parseFloat(customAmount) } : {}),
          ...(!isForever && editMaxCycles ? { maxCycles: parseInt(editMaxCycles, 10) } : {}),
          ...(() => {
            // Use custom override if entered, otherwise fall back to plan's setup fee
            const resolvedSetup = customSetupFee !== '' ? parseFloat(customSetupFee) : (selectedPlan.setupAmount ?? 0);
            return resolvedSetup > 0 ? { setupAmount: resolvedSetup } : {};
          })(),
        });
        // Fire-and-forget — never block the success screen on this.
        void autoSetHelcimSubscriberBilling(selectedCustomer.customerCode);
      }
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : isEditMode ? t('components.AddSubscriberModal.failedToUpdateSubscription') : t('components.AddSubscriberModal.failedToCreateSubscription'));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── render ──────────────────────────────────────────────────────────────────

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.modal}>
      <IonRow className={styles.modalWrapper}>
        <BasePageWithHeader
          title={isEditMode ? t('components.AddSubscriberModal.editSubscription') : t('components.AddSubscriberModal.addSubscriber')}
          toolbarClassName={styles.toolbar}
          startButtons={
            <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          }
          endButtons={
            !success
              ? (
                <IonButton
                  fill="solid"
                  color="primary"
                  disabled={!canSubmit}
                  onClick={handleSubmit}
                  className={styles.submitBtn}
                >
                  {submitting ? <IonSpinner name="bubbles" color={'primary'} style={{ width: 16, height: 16 }} /> : isEditMode ? t('components.AddSubscriberModal.save') : t('components.AddSubscriberModal.subscribe')}
                </IonButton>
              )
              : undefined
          }
        >
          {/* Portal root + anchored dropdowns live outside IonContent, as its
          siblings — same placement as HelcimSubscriptionsModal.tsx's
          pickerPortalRoot. IonContent's internal scroll containment traps
          position:fixed descendants nested inside it instead of letting them
          escape to the true viewport, which is what these portaled dropdown
          cards need. */}
          <div ref={setPickerPortalEl} className={styles.pickerPortalRoot} />

          <PortalDropdown
            isOpen={showPlanDropdown}
            anchorRef={planTriggerRef}
            portalRef={pickerPortalRef}
            onClose={() => setShowPlanDropdown(false)}
            value={selectedPlan ? String(selectedPlan.id) : ''}
            onSelect={(v) => {
              setShowPlanDropdown(false);
              const found = (allPlans ?? []).find(p => String(p.id) === v);
              if (found) { setSelectedPlan(found); setUseCustom(false); setCustomAmount(''); }
              else setSelectedPlan(null);
            }}
            options={[
              { value: '', label: t('components.AddSubscriberModal.selectAPlan') },
              ...(allPlans ?? []).map(p => ({ value: String(p.id), label: p.name ?? t('components.AddSubscriberModal.plan', { id: p.id }) })),
            ]}
          />

          <PortalDropdown
            isOpen={showPriceDropdown}
            anchorRef={priceTriggerRef}
            portalRef={pickerPortalRef}
            onClose={() => setShowPriceDropdown(false)}
            value={useCustom ? 'custom' : 'standard'}
            onSelect={(v) => {
              setShowPriceDropdown(false);
              if (v === 'custom') { setUseCustom(true); }
              else { setUseCustom(false); setCustomAmount(''); }
            }}
            options={[
              { value: 'standard', label: t('components.AddSubscriberModal.standard', { v1: planAmount.toFixed(2), v2: periodLabel(period, inc) }) },
              { value: 'custom', label: t('components.AddSubscriberModal.customAmount') },
            ]}
          />

          <IonContent className={styles.content}>
            <IonGrid className={styles.scrollContainer}>

              {/* ── Success Screen ──────────────────────────────────────────────── */}
              {success ? (
                <IonRow className={styles.successScreen}>
                  <IonRow className={styles.successIconWrap}>
                    <IonIcon icon={checkmarkCircleOutline} className={styles.successIcon} />
                  </IonRow>
                  <h2 className={styles.successTitle}>{isEditMode ? t('components.AddSubscriberModal.subscriptionUpdated') : t('components.AddSubscriberModal.subscriptionCreated')}</h2>
                  <IonText className={styles.successText}>
                    {isEditMode
                      ? <>{t('components.AddSubscriberModal.changesTo')} <strong>{selectedCustomer?.contactName ?? selectedCustomer?.customerCode}</strong>{t('components.AddSubscriberModal.sSubscriptionHaveBeenSaved')}</>
                      : <>{selectedCustomer?.contactName ?? selectedCustomer?.customerCode}{t('components.AddSubscriberModal.hasBeenAddedTo')} <strong>{selectedPlan?.name}</strong>.</>
                    }
                  </IonText>
                  <IonButton className={styles.successDoneBtn} onClick={onSubscriberAdded}>{t('components.AddSubscriberModal.done')}
                  </IonButton>
                </IonRow>
              ) : (
                <>
                  {/* ── Error ───────────────────────────────────────────────────── */}
                  {error && <IonText className={styles.errorBox}>{error}</IonText>}

                  {/* ── Section: Customer Information ─────────────────────────── */}
                  <IonCard className={`${styles.sectionCard} ${styles.customerCard}`}>
                    <p className={styles.sectionTitle}>{t('components.AddSubscriberModal.customerInformation')}</p>
                    <p className={styles.sectionSubtitle}>{t('components.AddSubscriberModal.searchForExistingCustomer')}</p>

                    {selectedCustomer ? (
                      <IonLabel className={styles.selectedCustomer}>
                        <IonAvatar className={styles.customerAvatar}>
                          <img src={proxyImage(selectedAvatar) || getDefaultAvatar(selectedCustomer.contactName || selectedCustomer.businessName || '')} alt="" />
                        </IonAvatar>
                        <IonLabel className={styles.customerInfo}>
                          <IonLabel className={styles.customerName}>
                            {selectedCustomer.contactName || selectedCustomer.businessName || selectedCustomer.customerCode}
                          </IonLabel>
                          {selectedCustomer.billingAddress?.email && (
                            <IonLabel className={styles.customerEmail}>{selectedCustomer.billingAddress.email}</IonLabel>
                          )}
                        </IonLabel>
                        {!isEditMode && (
                          <IonButton
                            fill="clear"
                            className={styles.removeBtn}
                            onClick={() => { setSelectedCustomer(null); setSearchActive(false); }}
                            aria-label={t('components.AddSubscriberModal.removeCustomer')}
                          >
                            <IonIcon icon={closeOutline} />
                          </IonButton>
                        )}
                      </IonLabel>
                    ) : !searchActive ? (
                      <IonButton
                        fill="clear"
                        className={styles.selectContactBtn}
                        onClick={() => { setSearchActive(true); runSearch(''); }}
                      >
                        <IonIcon icon={personCircleOutline} />{t('components.AddSubscriberModal.addCustomer')}
                      </IonButton>
                    ) : (
                      <IonItem ref={customerFieldRef} lines="none" className={styles.searchBox}>
                        <IonIcon icon={searchOutline} slot="start" className={styles.searchIcon} />
                        <IonInput
                          className={styles.searchInput}
                          value={searchQuery}
                          onIonInput={e => handleSearchChange(e.detail.value ?? '')}
                          onIonFocus={() => runSearch(searchQuery)}
                          placeholder={t('components.AddSubscriberModal.searchByNameEmailOrCustomer')}
                          autocomplete="off"
                          autofocus
                        />
                        <IonIcon icon={closeOutline} slot="end" className={styles.searchClear}
                          onClick={() => {
                            if (searchTimer.current) clearTimeout(searchTimer.current);
                            setSearchActive(false);
                            setShowDropdown(false);
                            setSearchQuery('');
                            setSearchResults([]);
                          }} />

                        {showDropdown && customerDropdownRect && pickerPortalNode && createPortal(
                          <>
                            <div className={styles.dropdownBackdrop} onClick={() => setShowDropdown(false)} />
                            <IonCard ref={customerDropdownCardRef} className={styles.calendarDropdown} style={{ ...customerDropdownRect, position: 'absolute' }}>
                              {searching ? (
                                <IonLabel className={styles.searchSpinnerWrap}>
                                  <IonSpinner name="bubbles" color="primary" />
                                </IonLabel>
                              ) : searchResults.length > 0 ? (
                                <IonList lines="none" className={styles.dropdownList}>
                                  {searchResults.map((c) => (
                                    <IonItem
                                      key={c.id}
                                      button
                                      detail={false}
                                      lines="none"
                                      className={styles.dropdownItem}
                                      onClick={() => selectCustomer(c)}
                                    >
                                      <IonAvatar slot="start">
                                        <img src={proxyImage(c.avatar ?? '') || getDefaultAvatar(c.displayName || `${c.firstName} ${c.lastName}`.trim())} alt="" />
                                      </IonAvatar>
                                      <IonLabel>
                                        <h3>{c.displayName || `${c.firstName} ${c.lastName}`.trim() || c.company}</h3>
                                        {(c.primaryEmail || c.primaryPhone) && <p>{c.primaryEmail || c.primaryPhone}</p>}
                                      </IonLabel>
                                    </IonItem>
                                  ))}
                                </IonList>
                              ) : (
                                <IonLabel className={styles.searchEmpty}>{t('components.AddSubscriberModal.noCustomersFound')}</IonLabel>
                              )}
                            </IonCard>
                          </>,
                          pickerPortalNode,
                        )}
                      </IonItem>
                    )}

                  </IonCard>{/* end sectionCard: Customer */}

                  {/* ── Section: Pricing ──────────────────────────────────────── */}
                  <IonCard className={styles.sectionCard}>
                    <p className={`${styles.sectionTitle} ${styles.mb8}`}>{isEditMode ? t('components.AddSubscriberModal.planDetails') : t('components.AddSubscriberModal.pricing')}</p>
                    {isEditMode && <p className={styles.sectionSubtitle}>{t('components.AddSubscriberModal.theAmountTheCustomerWillBe')}</p>}

                    {/* Plan Name */}
                    <p className={styles.subHeading}>{t('components.AddSubscriberModal.planName')}</p>
                    <p className={styles.subSubtitle}>{t('components.AddSubscriberModal.selectedPaymentPlan')}</p>
                    {allPlans && allPlans.length > 1 && (!isEditMode || isNotYetActivated) ? (
                      <button
                        ref={planTriggerRef}
                        type="button"
                        className={`${styles.fieldBoxInfo} ${styles.mb18} ${styles.dropdownTrigger}`}
                        onClick={() => setShowPlanDropdown(v => !v)}
                      >
                        <span style={!selectedPlan ? { color: 'var(--ion-color-medium)' } : undefined}>
                          {selectedPlan ? (selectedPlan.name ?? t('components.AddSubscriberModal.plan', { id: selectedPlan.id })) : t('components.AddSubscriberModal.selectAPlan')}
                        </span>
                        <IonIcon icon={chevronDownOutline} style={{ color: 'var(--ion-color-medium)', fontSize: 16, flexShrink: 0 }} />
                      </button>
                    ) : (
                      <IonRow className={`${styles.fieldBoxInfo} ${styles.mb18}`} style={isEditMode && !isNotYetActivated ? { opacity: 0.45 } : undefined}>
                        <span>{selectedPlan ? (selectedPlan.name ?? t('components.AddSubscriberModal.plan', { id: selectedPlan.id })) : t('components.AddSubscriberModal.noPlan')}</span>
                        <IonIcon icon={pricetagOutline} style={{ color: 'var(--ion-color-medium)', fontSize: 16 }} />
                      </IonRow>
                    )}

                    {/* Plan Price */}
                    <p className={styles.subHeading}>{t('components.AddSubscriberModal.planPrice')}</p>
                    <p className={styles.subSubtitle}>{t('components.AddSubscriberModal.standardOrCustomRecurringAmount')}</p>

                    {/* Dropdown */}
                    <button
                      ref={priceTriggerRef}
                      type="button"
                      className={`${styles.fieldBoxInfo} ${styles.mb18} ${styles.dropdownTrigger}`}
                      onClick={() => setShowPriceDropdown(v => !v)}
                    >
                      <span>
                        {useCustom ? t('components.AddSubscriberModal.custom') : `$${planAmount.toFixed(2)} / ${periodLabel(period, inc)}`}
                      </span>
                      <IonIcon icon={chevronDownOutline} style={{ color: 'var(--ion-color-medium)', fontSize: 16, flexShrink: 0 }} />
                    </button>

                    {useCustom && (
                      <IonRow className={`${styles.customAmountBox} ${styles.mb18}`}>
                        <span style={{ color: 'var(--ion-color-medium)', fontSize: 16 }}>$</span>
                        <input
                          className={styles.customAmountInput}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={planAmount.toFixed(2)}
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                        />
                        {customAmount && (
                          <IonButton fill="clear" size="small" className={styles.resetBtn} onClick={() => setCustomAmount('')}>{t('components.AddSubscriberModal.reset')}</IonButton>
                        )}
                      </IonRow>
                    )}

                    {/* Setup fee — hidden in edit mode when plan has no setup fee */}
                    {(!isEditMode || setupFee > 0) && (
                      <>
                        <p className={styles.subHeading}>{t('components.AddSubscriberModal.setupFee')}</p>
                        <p className={styles.subSubtitle}>{t('components.AddSubscriberModal.oneTimeFeeChargedAtActivation')}</p>
                        <IonRow className={`${styles.setupFeeBox} ${styles.mb18}`}>
                          <span style={{ color: 'var(--ion-color-medium)', fontSize: 16, flexShrink: 0 }}>$</span>
                          <input
                            className={styles.customAmountInput}
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={setupFee.toFixed(2)}
                            value={customSetupFee}
                            onChange={(e) => setCustomSetupFee(e.target.value)}
                          />
                          {customSetupFee && (
                            <IonButton fill="clear" size="small" className={styles.resetBtn} onClick={() => setCustomSetupFee('')}>{t('components.AddSubscriberModal.reset')}</IonButton>
                          )}
                        </IonRow>
                      </>
                    )}

                  </IonCard>{/* end sectionCard: Pricing */}

                  {/* ── Section: Subscription Schedule ────────────────────────── */}
                  <IonCard className={styles.sectionCard}>
                    <p className={styles.sectionTitle}>{t('components.AddSubscriberModal.subscriptionSchedule')}</p>
                    <p className={styles.sectionSubtitle}>{t('components.AddSubscriberModal.whenAndHowOftenChargesWill')}</p>

                    {/* Term Length */}
                    <p className={styles.subHeading}>{t('components.AddSubscriberModal.termLength')}</p>
                    <p className={styles.subSubtitle}>{t('components.AddSubscriberModal.durationOfSubscription')}</p>
                    {!isForever ? (
                      <IonRow className={`${styles.cyclesStepper} ${styles.mb18}`}>
                        <IonRow className={styles.stepperInputWrap}>
                          <input
                            className={styles.stepperInput}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder={isEditMode ? String(editSubscription?.maxCycles ?? '1') : '1'}
                            value={editMaxCycles}
                            onChange={(e) => setEditMaxCycles(e.target.value.replace(/[^0-9]/g, ''))}
                          />
                          <IonCol className={styles.spinBtns}>
                            <IonButton fill="clear" type="button" className={styles.spinUp}
                              onClick={() => setEditMaxCycles(v => String((parseInt(v || '0', 10) || 0) + 1))}>
                              <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 5L5 1L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </IonButton>
                            <IonButton fill="clear" type="button" className={styles.spinDown}
                              onClick={() => setEditMaxCycles(v => String(Math.max(1, (parseInt(v || '1', 10) || 1) - 1)))}>
                              <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </IonButton>
                          </IonCol>
                        </IonRow>
                        <span className={styles.stepperLabel}>{t('components.AddSubscriberModal.cycle')}{parseInt(editMaxCycles || '1', 10) !== 1 ? 's' : ''}</span>
                      </IonRow>
                    ) : (
                      <IonRow className={`${styles.fieldBox} ${styles.fieldBoxMuted} ${styles.mb18}`}>
                        <span>{t('components.AddSubscriberModal.forever')}</span>
                        <IonIcon icon={timeOutline} style={{ color: 'var(--ion-color-medium)', fontSize: 16 }} />
                      </IonRow>
                    )}

                    {/* Activation Day — read-only in edit mode unless not yet activated */}
                    <p className={styles.subHeading}>{t('components.AddSubscriberModal.activationDay')}</p>
                    <p className={styles.subSubtitle}>
                      {isEditMode ? t('components.AddSubscriberModal.dateSubscriptionBecameActive') : t('components.AddSubscriberModal.dateFirstChargeIsApplied')}
                    </p>
                    {isEditMode && !isNotYetActivated ? (
                      <IonRow className={`${styles.fieldBoxInfo} ${styles.mb18}`} style={{ opacity: 0.45 }}>
                        <span>{displayDate(activationDate)}</span>
                        <IonIcon icon={timeOutline} style={{ color: 'var(--ion-color-medium)', fontSize: 16 }} />
                      </IonRow>
                    ) : (
                      <>
                        <button
                          ref={dateTriggerRef}
                          type="button"
                          className={`${styles.fieldBoxInfo} ${styles.mb8} ${styles.dropdownTrigger}`}
                          onClick={() => setShowDatePicker(v => !v)}
                        >
                          <span>
                            {activationDate === fmtDate(new Date()) ? t('components.AddSubscriberModal.today') : displayDate(activationDate)}
                          </span>
                          <IonIcon icon={calendarOutline} style={{ color: 'var(--ion-color-medium)', fontSize: 16, flexShrink: 0 }} />
                        </button>

                        {showDatePicker && dateDropdownRect && pickerPortalNode && createPortal(
                          <>
                            <div className={styles.dropdownBackdrop} onClick={() => setShowDatePicker(false)} />
                            <IonCard ref={dateDropdownCardRef} className={`${styles.calendarDropdown} ${styles.dateCalendarDropdown}`} style={{ ...dateDropdownRect, position: 'absolute' }}>
                              <MiniCalendarPicker
                                desktop
                                value={new Date(`${activationDate}T00:00:00`)}
                                onSelect={(d) => {
                                  const picked = fmtDate(d);
                                  if (picked >= fmtDate(new Date())) {
                                    setActivationDate(picked);
                                    setShowDatePicker(false);
                                  }
                                }}
                              />
                            </IonCard>
                          </>,
                          pickerPortalNode,
                        )}

                        <IonRow className={styles.infoRow}>
                          <IonIcon icon={informationCircleOutline} />
                          <span>{t('components.AddSubscriberModal.subscriptionActivatesOn')} {activationDate === fmtDate(new Date()) ? t('components.AddSubscriberModal.today') : displayDate(activationDate)}</span>
                        </IonRow>
                      </>
                    )}

                  </IonCard>{/* end sectionCard: Schedule */}

                  {/* ── Section: Payment Method ──────────────────────────────── */}
                  <IonCard className={styles.sectionCard}>
                    <p className={styles.sectionTitle}>{t('components.AddSubscriberModal.paymentMethod')}</p>
                    <p className={styles.sectionSubtitle}>{t('components.AddSubscriberModal.howTheCustomerWillBeCharged')}</p>

                    <IonRow className={styles.payMethodRow}>
                      <IonButton
                        fill="clear"
                        className={`${styles.payMethodBtn}${payMethod === 'credit' ? ` ${styles.active}` : ''}${freshCards.length === 0 && !loadingCards ? ` ${styles.disabled}` : ''}`}
                        onClick={() => freshCards.length > 0 && setPayMethod('credit')}
                        disabled={freshCards.length === 0 && !loadingCards}
                      >
                        <IonRadio ref={creditRadioRef} value="credit" className={styles.radioCircle} />
                        <IonIcon icon={cardOutline} style={{ fontSize: 18 }} />
                        <span>{t('components.AddSubscriberModal.creditCard')}{freshCards.length === 0 && !loadingCards && selectedCustomer ? t('components.AddSubscriberModal.none') : ''}</span>
                      </IonButton>
                      <IonButton
                        fill="clear"
                        className={`${styles.payMethodBtn}${payMethod === 'ach' ? ` ${styles.active}` : ''}${freshBanks.length === 0 && !loadingCards ? ` ${styles.disabled}` : ''}`}
                        onClick={() => freshBanks.length > 0 && setPayMethod('ach')}
                        disabled={freshBanks.length === 0 && !loadingCards}
                      >
                        <IonRadio ref={achRadioRef} value="ach" className={styles.radioCircle} />
                        <IonIcon icon={businessOutline} style={{ fontSize: 18 }} />
                        <span>{t('components.AddSubscriberModal.achBank')}{freshBanks.length === 0 && !loadingCards && selectedCustomer ? t('components.AddSubscriberModal.none') : ''}</span>
                      </IonButton>
                    </IonRow>

                    {/* Card / bank on file */}
                    {selectedCustomer && (() => {
                      const cards = freshCards;
                      const banks = freshBanks;
                      if (loadingCards) return <IonRow className={styles.cardLoadingRow}><IonSpinner name="bubbles" color="primary" style={{ width: 16, height: 16 }} /><span>{t('components.AddSubscriberModal.loadingPaymentMethods')}</span></IonRow>;
                      // Pick default card — try isDefault/default/setDefault flag, fall back to first
                      const card = cards.find(c =>
                        c['isDefault'] === 1 || c['isDefault'] === true ||
                        c['default'] === 1 || c['default'] === true ||
                        c['setDefault'] === 1 || c['setDefault'] === true
                      ) ?? cards[0];
                      // Pick default bank account
                      const bank = banks.find(b =>
                        b['isDefault'] === 1 || b['isDefault'] === true ||
                        b['default'] === 1 || b['default'] === true
                      ) ?? banks[0];
                      const showCard = payMethod === 'credit' && card;
                      const showBank = payMethod === 'ach' && bank;
                      if (!showCard && !showBank) return null;
                      if (showCard) {
                        // cardF6L4 = first 6 and last 4 digits (e.g. "411111######1111")
                        const cardF6L4 = (
                          (card['cardF6L4'] as string | undefined) ??
                          (card['cardNumber'] as string | undefined) ??
                          (card['maskedCardNumber'] as string | undefined) ??
                          ''
                        );
                        const last4 = cardF6L4.slice(-4);
                        const firstDigit = cardF6L4.replace(/\D/g, '')[0] ?? '';
                        const brandLabel = firstDigit === '4' ? t('components.AddSubscriberModal.visa2')
                          : firstDigit === '5' ? t('components.AddSubscriberModal.mastercard')
                            : firstDigit === '3' ? t('components.AddSubscriberModal.amex')
                              : firstDigit === '6' ? t('components.AddSubscriberModal.discover2')
                                : '';
                        const brandLogo: Record<string, React.ReactNode> = {
                          Visa: (
                            <svg viewBox="0 0 60 20" width="48" height="16" xmlns="http://www.w3.org/2000/svg">
                              <rect width="60" height="20" rx="3" fill="#FFFFFF" stroke="#e0e0e0" strokeWidth="1" />
                              <text x="6" y="15" fontFamily="Arial,Helvetica,sans-serif" fontWeight="900" fontStyle="italic" fontSize="14" fill="#1A1F71" letterSpacing="1">{t('components.AddSubscriberModal.visa')}</text>
                            </svg>
                          ),
                          Mastercard: (
                            <svg viewBox="0 0 38 24" width="38" height="24" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="14" cy="12" r="10" fill="#EB001B" />
                              <circle cx="24" cy="12" r="10" fill="#F79E1B" />
                              <path d="M19 4.8a10 10 0 0 1 0 14.4A10 10 0 0 1 19 4.8z" fill="#FF5F00" />
                            </svg>
                          ),
                          Amex: (
                            <svg viewBox="0 0 110 52" width="110" height="52" xmlns="http://www.w3.org/2000/svg">
                              <rect width="110" height="52" rx="5" fill="#2563EB" />
                              <text x="55" y="20" textAnchor="middle" dominantBaseline="middle" fontFamily="Arial,Helvetica,sans-serif" fontWeight="900" fontSize="13" fill="none" stroke="#FFFFFF" strokeWidth="0.8" letterSpacing="1.5">{t('components.AddSubscriberModal.american')}</text>
                              <text x="60" y="37" textAnchor="middle" dominantBaseline="middle" fontFamily="Arial,Helvetica,sans-serif" fontWeight="900" fontSize="11" fill="none" stroke="#FFFFFF" strokeWidth="0.7" letterSpacing="1.5">{t('components.AddSubscriberModal.express')}</text>
                            </svg>
                          ),
                          Discover: (
                            <svg viewBox="0 0 60 20" width="48" height="16" xmlns="http://www.w3.org/2000/svg">
                              <rect width="60" height="20" rx="3" fill="#FFFFFF" stroke="#ddd" strokeWidth="1" />
                              <text x="3" y="14" fontFamily="Arial,Helvetica,sans-serif" fontWeight="bold" fontSize="8" fill="#231F20" letterSpacing="0.3">{t('components.AddSubscriberModal.discover')}</text>
                              <circle cx="52" cy="10" r="7" fill="#F76F20" />
                            </svg>
                          ),
                        };
                        const exp = (card['cardExpiry'] as string | undefined) ?? '';
                        const _name = (
                          (card['cardHolderName'] as string | undefined) ??
                          (card['holderName'] as string | undefined) ??
                          selectedCustomer.contactName ?? ''
                        );
                        const holderName = _name;
                        const expFmt = exp.length === 4 ? `${exp.slice(0, 2)}/${exp.slice(2)}` : exp;
                        return (
                          <>
                            <IonRow className={styles.paymentOnFile}>
                              <IonRow className={styles.paymentOnFileLeft}>
                                <IonRow className={styles.paymentOnFileLogo}>{brandLogo[brandLabel] ?? <span style={{ fontSize: 20 }}>💳</span>}</IonRow>
                                <IonCol className={styles.paymentOnFileDetails}>
                                  <span className={styles.paymentOnFileLabel}>{t('components.AddSubscriberModal.cardNumber')}</span>
                                  <span className={styles.paymentOnFileNum}>•••• {last4}</span>
                                </IonCol>
                              </IonRow>
                              {expFmt && (
                                <IonCol className={styles.paymentOnFileRight}>
                                  <span className={styles.paymentOnFileLabel}>{t('components.AddSubscriberModal.mmYy')}</span>
                                  <span className={styles.paymentOnFileNum}>{expFmt}</span>
                                </IonCol>
                              )}
                            </IonRow>
                            {holderName && (
                              <IonRow className={styles.paymentOnFile} style={{ marginTop: 4 }}>
                                <IonRow className={styles.paymentOnFileLeft}>
                                  <IonRow className={styles.paymentOnFileAvatar}>
                                    {holderName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                                  </IonRow>
                                  <IonCol className={styles.paymentOnFileDetails}>
                                    <span className={styles.paymentOnFileLabel}>{t('components.AddSubscriberModal.cardholderName')}</span>
                                    <span className={styles.paymentOnFileNum}>{holderName}</span>
                                  </IonCol>
                                </IonRow>
                              </IonRow>
                            )}
                          </>
                        );
                      }
                      if (showBank) {
                        const acct = (
                          (bank['bankAccountNumberL4'] as string | undefined) ??
                          (bank['bankAccountNumber'] as string | undefined) ??
                          (bank['accountNumber'] as string | undefined) ??
                          ''
                        );
                        const _rout = (
                          (bank['routingNumber'] as string | undefined) ??
                          (bank['bankRoutingNumber'] as string | undefined) ??
                          ''
                        );
                        const _bname = (
                          (bank['accountHolderName'] as string | undefined) ??
                          (bank['holderName'] as string | undefined) ??
                          selectedCustomer.contactName ?? ''
                        );
                        return (
                          <IonRow className={styles.paymentOnFile}>
                            <IonRow className={styles.paymentOnFileLeft}>
                              <IonRow className={styles.paymentOnFileLogo}>
                                <IonIcon icon={businessOutline} style={{ fontSize: 26, color: 'var(--ion-color-primary)' }} />
                              </IonRow>
                              <IonCol className={styles.paymentOnFileDetails}>
                                <span className={styles.paymentOnFileLabel}>{t('components.AddSubscriberModal.bankAccount')}</span>
                                <span className={styles.paymentOnFileNum}>•••• {acct.slice(-4) || acct}</span>
                              </IonCol>
                            </IonRow>
                          </IonRow>
                        );
                      }
                      return null;
                    })()}

                    <IonRow className={`${styles.infoRow} ${styles.mb18}`} style={{ marginTop: 8 }}>
                      <IonIcon icon={informationCircleOutline} />
                      <span>{t('components.AddSubscriberModal.paymentMethodOnFileWillBe')}</span>
                    </IonRow>

                  </IonCard>{/* end sectionCard: Payment Method */}

                  {/* ── Section: Subscription Summary ──────────────────────────── */}
                  <p className={styles.sectionTitle} style={{ marginTop: 20 }}>{t('components.AddSubscriberModal.subscriptionSummary')}</p>
                  <p className={styles.sectionSubtitle}>{t('components.AddSubscriberModal.reviewBeforeConfirming')}</p>

                  {!selectedPlan ? (
                    <IonRow className={styles.summaryEmpty}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="var(--ion-color-primary)" strokeWidth="1.5" />
                        <path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="var(--ion-color-primary)" strokeWidth="1.5" strokeLinecap="round" />
                        <circle cx="9" cy="10" r="1" fill="var(--ion-color-primary)" />
                        <circle cx="15" cy="10" r="1" fill="var(--ion-color-primary)" />
                      </svg>
                      <p className={styles.summaryEmptyTitle}>{t('components.AddSubscriberModal.noSummaryYet')}</p>
                      <p className={styles.summaryEmptySubtitle}>{t('components.AddSubscriberModal.pleaseTellUsAboutYourGreat')}</p>
                    </IonRow>
                  ) : (
                    <IonCard className={styles.summaryCard}>
                      {/* Hero amount */}
                      <IonRow className={styles.summaryHero}>
                        <span className={styles.summaryPeriodLabel}>{periodLabel(period, inc).toUpperCase()}</span>
                        <span className={styles.summaryAmount}>${baseAmount.toFixed(2)}</span>
                        <span className={styles.summaryCurrency}>{t('components.AddSubscriberModal.usd')}</span>
                      </IonRow>

                      {/* Detail rows */}
                      <IonRow className={styles.summaryRows}>
                        {/* Plan Name */}
                        <IonRow className={styles.summaryRow}>
                          <span className={styles.summaryRowLabel}>{t('components.AddSubscriberModal.planName')}</span>
                          <span className={styles.summaryRowValue}>{(plan ?? selectedPlan).name ?? t('components.AddSubscriberModal.plan2', { v1: (plan ?? selectedPlan).id })}</span>
                        </IonRow>

                        {/* Activation Date */}
                        <IonRow className={styles.summaryRow}>
                          <IonRow className={styles.summaryRowLabel}>
                            <IonIcon icon={checkmarkCircleOutline} />
                            <span>{t('components.AddSubscriberModal.activationDate')}</span>
                          </IonRow>
                          <span className={styles.summaryRowValue}>{displayDate(activationDate)}</span>
                        </IonRow>

                        {/* Term Length */}
                        <IonRow className={styles.summaryRow}>
                          <IonRow className={styles.summaryRowLabel}>
                            <IonIcon icon={refreshOutline} />
                            <span>{t('components.AddSubscriberModal.termLength')}</span>
                          </IonRow>
                          <span className={styles.summaryRowValue}>{isForever ? t('components.AddSubscriberModal.forever') : maxCyclesNum}</span>
                        </IonRow>

                        {/* Expiry */}
                        <IonRow className={styles.summaryRow}>
                          <IonRow className={styles.summaryRowLabel}>
                            <IonIcon icon={exitOutline} />
                            <span>{t('components.AddSubscriberModal.expiry')}</span>
                          </IonRow>
                          <span className={styles.summaryRowValue}>{expiryDate ? displayDate(fmtDate(expiryDate)) : t('components.AddSubscriberModal.no')}</span>
                        </IonRow>

                        {/* First Billing Day */}
                        <IonRow className={styles.summaryRow}>
                          <IonRow className={styles.summaryRowLabel}>
                            <IonIcon icon={calendarOutline} />
                            <span>{t('components.AddSubscriberModal.firstBillingDay')}</span>
                          </IonRow>
                          <span className={styles.summaryRowValue}>{displayDate(fmtDate(firstBill))}</span>
                        </IonRow>

                        {/* Second Billing Day */}
                        {(displayCycles === null || displayCycles >= 2) && (
                          <IonRow className={styles.summaryRow}>
                            <IonRow className={styles.summaryRowLabel}>
                              <IonIcon icon={calendarOutline} />
                              <span>{t('components.AddSubscriberModal.secondBillingDay')}</span>
                            </IonRow>
                            <span className={styles.summaryRowValue}>{displayDate(fmtDate(secondBill))}</span>
                          </IonRow>
                        )}
                      </IonRow>

                      {/* Setup Fee row */}
                      {!isEditMode && resolvedSetupFee > 0 && (
                        <IonRow className={styles.timelineRecurringRow}>
                          <span className={styles.timelineRecurringLabel}>{t('components.AddSubscriberModal.setUpFee')} <span className={styles.timelineRecurringMeta}>{t('components.AddSubscriberModal.chargedOnFirstBilling')}</span></span>
                          <span className={styles.timelineRecurringAmount}>${resolvedSetupFee.toFixed(2)}</span>
                        </IonRow>
                      )}

                      {/* First Payment row (shown when setup fee adds to first charge) */}
                      {!isEditMode && resolvedSetupFee > 0 && (
                        <IonRow className={styles.timelineRecurringRow}>
                          <span className={styles.timelineRecurringLabel}>{t('components.AddSubscriberModal.firstPayment')} <span className={styles.timelineRecurringMeta}>{t('components.AddSubscriberModal.includingTaxes')}</span></span>
                          <span className={styles.timelineRecurringAmount}><strong>${firstChargeAmount.toFixed(2)}</strong></span>
                        </IonRow>
                      )}

                      {/* Recurring Payment row */}
                      <IonRow className={styles.timelineRecurringRow}>
                        <span className={styles.timelineRecurringLabel}>{t('components.AddSubscriberModal.recurringPayment')} <span className={styles.timelineRecurringMeta}>{t('components.AddSubscriberModal.includingTaxes')}</span></span>
                        <span className={styles.timelineRecurringAmount}><strong>${effectiveAmount.toFixed(2)}</strong></span>
                      </IonRow>

                      {/* Timeline — hidden in edit mode */}
                      {!isEditMode && (
                        <IonRow className={styles.timeline}>
                          <IonRow className={styles.timelineGrid}>
                            {/* Node 0: Plan Activation */}
                            <IonCol className={styles.timelineCell}>
                              <span className={styles.timelineDot} />
                              <IonText className={styles.timelineAmount}>${activationIsBillingDay ? firstChargeAmount.toFixed(2) : '0.00'}</IonText>
                              <IonText className={styles.timelineLabel}>{t('components.AddSubscriberModal.planActivation')}</IonText>
                              <IonText className={styles.timelineDate}>{displayDate(activationDate)}</IonText>
                            </IonCol>

                            {activationIsBillingDay ? (
                              <>
                                {/* Bills on sign-up: Activation already billed, so next nodes are 2nd and 3rd billing */}
                                {(displayCycles === null || displayCycles >= 2) && (
                                  <IonCol className={styles.timelineCell}>
                                    <span className={displayCycles === null || displayCycles > 2 ? styles.timelineDotDashed : styles.timelineDotHollow} />
                                    <IonText className={styles.timelineAmount}>${effectiveAmount.toFixed(2)}</IonText>
                                    <IonText className={styles.timelineLabel}>{t('components.AddSubscriberModal.2ndBilling')}</IonText>
                                    <IonText className={styles.timelineDate}>{displayDate(fmtDate(secondBill))}</IonText>
                                  </IonCol>
                                )}
                                {(displayCycles === null || displayCycles >= 3) && (
                                  <IonCol className={styles.timelineCell}>
                                    <span className={displayCycles === null || displayCycles > 3 ? styles.timelineDotDashed : styles.timelineDotHollow} />
                                    <IonText className={styles.timelineAmount}>${effectiveAmount.toFixed(2)}</IonText>
                                    <IonText className={styles.timelineLabel}>{t('components.AddSubscriberModal.3rdBilling')}</IonText>
                                    <IonText className={styles.timelineDate}>{displayDate(fmtDate(thirdBill))}</IonText>
                                  </IonCol>
                                )}
                              </>
                            ) : (
                              <>
                                {/* Deferred billing: Activation is $0, first bill is on the billing day */}
                                {(displayCycles === null || displayCycles >= 1) && (
                                  <IonCol className={styles.timelineCell}>
                                    <span className={styles.timelineDotHollow} />
                                    <IonText className={styles.timelineAmount}>${firstChargeAmount.toFixed(2)}</IonText>
                                    <IonText className={styles.timelineLabel}>{t('components.AddSubscriberModal.1stBilling')}</IonText>
                                    <IonText className={styles.timelineDate}>{displayDate(fmtDate(firstBill))}</IonText>
                                  </IonCol>
                                )}
                                {(displayCycles === null || displayCycles >= 2) && (
                                  <IonCol className={styles.timelineCell}>
                                    <span className={displayCycles === null || displayCycles > 2 ? styles.timelineDotDashed : styles.timelineDotHollow} />
                                    <IonText className={styles.timelineAmount}>${effectiveAmount.toFixed(2)}</IonText>
                                    <IonText className={styles.timelineLabel}>{t('components.AddSubscriberModal.2ndBilling')}</IonText>
                                    <IonText className={styles.timelineDate}>{displayDate(fmtDate(secondBill))}</IonText>
                                  </IonCol>
                                )}
                              </>
                            )}
                          </IonRow>
                        </IonRow>
                      )}
                      <p className={styles.timelineFootnote}>{t('components.AddSubscriberModal.theAmountsShownAboveIncludeApplicable')}</p>
                    </IonCard>
                  )}
                </>
              )}
            </IonGrid>
          </IonContent>
        </BasePageWithHeader>
      </IonRow>
    </IonModal>
  );
};

export default AddSubscriberModal;
